import { Clock, Email, Eye, Note, User } from "@livingdesign/icons";
import CampaignEditChannel from "../components/campaign/CampaignEditChannel";
import CampaignEditDataConnection from "../components/campaign/CampaignEditDataConnection";
import CampaignEditDetails from "../components/campaign/CampaignEditDetails";
import CampaignEditGeneralDataConnection from "../components/campaign/CampaignEditGeneralDataConnection";
import CampaignEditReview from "../components/campaign/CampaignEditReview";
import CampaignEditTemplate from "../components/campaign/CampaignEditTemplate";
import CampaignEditType from "../components/campaign/CampaignEditType";
import {
  getCampaignHeadings,
  getCampaignType,
  getDataSourceType,
  getNotification,
} from "../utils/campaign";
import {
  isValidMailNotification,
  getTemplateContent,
  isValidZoomTeamNotification,
} from "../utils/notification";
import { CAMPAIGN_TYPES, NOTIFICATION_CHANNELS, SNIPPET_MAX_CHAR, SUBJECT_MAX_CHAR, UI_DATA } from "./Campaign";
import {
  CAMPAIGN,
  CHANNELS,
  CONFIGURATIONS,
  DATA_SOURCE,
  DATA_SOURCE_TYPES,
  NOTIFICATION,
  RECIPIENTS,
} from "./DBFields";
import MessageDetailsEdit from "../components/v2/Campaign/CampaignEdit/EditLayout/MessageDetailsEdit";
import SelectRecipientsEdit from "../components/v2/Campaign/CampaignEdit/EditLayout/SelectRecipientsEdit";
import DesignContentEdit from "../components/v2/Campaign/CampaignEdit/EditLayout/DesignContentEdit";
import DeliveryTimeEdit from "../components/v2/Campaign/CampaignEdit/EditLayout/DeliveryTimeEdit";
import ReviewMessageEdit from "../components/v2/Campaign/CampaignEdit/EditLayout/ReviewMessageEdit";
import { validateTime } from "../utils/campaignEdit";
import MsTeamsNotificationEdit from "../components/v2/Campaign/CampaignEdit/EditLayout/MsTeamsNotificationEdit";
import * as yup from "yup";

export const BUTTONS = {
  SAVE_DRAFT: "Save as Draft",
  SAVE_CHANGES: "Save Changes",
  TEST: "Test Message",
  SEND: {
    label: "Send Options",
    options: ["Send now", "Schedule for later"],
  },
  SAVE_NOTIFICATIONS: "Complete Draft",
};

const STEPS = {
  CHANNEL: {
    label: "Channel",
    title: "Where do you want to reach your recipients?",
    required: true,
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0) => {
      const notification =
        campaign?.[CAMPAIGN.NOTIFICATIONS]?.[notificationIndex];
      return notification?.[NOTIFICATION.TYPE] ? true : false;
    },
    component: (props) => <CampaignEditChannel {...props} />,
    review: true,
  },
  CAMPAIGN_TYPE: {
    label: "Connection",
    title: "What kind of campaign are you sending?",
    required: true,
    isComplete: (campaign) => {
      return campaign?.[CAMPAIGN.DATA_SOURCE]?.[DATA_SOURCE.TYPE]
        ? true
        : false;
    },
    component: (props) => <CampaignEditType {...props} />,
    review: true,
  },
  DATA_CONNECT: {
    label: "Data",
    title: "What data source would you like to use?",
    required: false,
    isComplete: (campaign) => {
      switch (getDataSourceType(campaign)) {
        case DATA_SOURCE_TYPES.CSV:
          if (
            campaign?.[CAMPAIGN.METADATA]?.[CAMPAIGN.METADATA_HEADINGS]?.length
          ) {
            return true;
          }
          return false;
        default:
          return false;
      }
    },
    component: (props) => <CampaignEditDataConnection {...props} />,
  },
  GENERAL_DATA_CONNECT: {
    label: "Data",
    title: "What data source would you like to use?",
    required: false,
    isComplete: (campaign) => {
      if (campaign.general_data_source_type) return true;
      if (campaign?.[CAMPAIGN.METADATA]?.[CAMPAIGN.METADATA_HEADINGS]?.length) {
        return true;
      }
      return false;
    },
    component: (props) => <CampaignEditGeneralDataConnection {...props} />,
    fields: [
      RECIPIENTS.TO,
      RECIPIENTS.CC,
      RECIPIENTS.BCC,
      CONFIGURATIONS.SUBJECT,
      UI_DATA.SNIPPET_TEXT,
      CONFIGURATIONS.FROM,
    ],
  },
  EMAIL_CONFIG: {
    label: "Details",
    title: "Tell us the details of your message",
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0, hasExternalUsersToggle=false) => {
      const notification = getNotification(campaign, notificationIndex);
      let isSystemNotifications = false;
      if(campaign?.[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS]) {
          isSystemNotifications = true;
      } 
      return isValidMailNotification(
        notification,
        getCampaignHeadings(campaign),
        hasExternalUsersToggle,
        getCampaignType(campaign),
        isSystemNotifications
      );
    },
    component: (props) => <CampaignEditDetails {...props} />,
    review: true,
    fields: [
      RECIPIENTS.TO,
      RECIPIENTS.CC,
      RECIPIENTS.BCC,
      CONFIGURATIONS.SUBJECT,
      UI_DATA.SNIPPET_TEXT,
      CONFIGURATIONS.FROM,
    ],
  },
  ZOOM_CONFIG: {
    label: "Details",
    title: "Tell us the details of your message",
    isComplete: (campaign, notificationIndex = 0) => {
      const notification = getNotification(campaign, notificationIndex);
      return isValidZoomTeamNotification(
        notification,
        getCampaignHeadings(campaign)
      );
    },
    component: (props) => <CampaignEditDetails {...props} />,
    review: true,
    fields: [RECIPIENTS.TO],
  },
  EMAIL_TEMPLATE: {
    label: "Design",
    title: "Design your content",
    titlePostfix: " - Template needs to be saved.",
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0) => {
      const notification = getNotification(campaign, notificationIndex);
      if (getTemplateContent(notification)) {
        return true && !needToSaveTemplate;
      }
      return false;
    },
    component: (props) => <CampaignEditTemplate {...props} />,
    review: true,
  },
  ZOOM_TEMPLATE: {
    label: "Content",
    title: "Build your notification",
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0) => {
      const notification = getNotification(campaign, notificationIndex);
      if (getTemplateContent(notification)) {
        return true;
      }
      return false;
    },
    component: (props) => <CampaignEditTemplate {...props} />,
    review: true,
  },
  REVIEW: {
    label: "Review",
    isComplete: () => false,
    component: () => <CampaignEditReview />,
  },
};

const SYSTEM_NOTIFICATIONS_STEPS = {
  CHANNEL: {
    label: "Channel",
    title: "Where do you want to reach your recipients?",
    required: true,
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0) => {
      const notification =
        campaign?.[CAMPAIGN.NOTIFICATIONS]?.[notificationIndex];
      return notification?.[NOTIFICATION.TYPE] ? true : false;
    },
    component: (props) => <CampaignEditChannel {...props} />,
    review: true,
  },
  CAMPAIGN_TYPE: {
    label: "Connection",
    title: "What kind of campaign are you sending?",
    required: true,
    isComplete: (campaign) => {
      return campaign?.[CAMPAIGN.DATA_SOURCE]?.[DATA_SOURCE.TYPE]
        ? true
        : false;
    },
    component: (props) => <CampaignEditType {...props} />,
    review: true,
  },
  DATA_CONNECT: {
    label: "Data",
    title: "Please add personalized data fields in CSV file here",
    required: false,
    isComplete: (campaign) => {
      switch (getDataSourceType(campaign)) {
        case DATA_SOURCE_TYPES.CSV:
          if (
            campaign?.[CAMPAIGN.METADATA]?.[CAMPAIGN.METADATA_HEADINGS]?.length
          ) {
            return true;
          }
          return false;
        default:
          return false;
      }
    },
    component: (props) => <CampaignEditDataConnection {...props} />,
  },
  GENERAL_DATA_CONNECT: {
    label: "Data",
    title: "Please add personalized data fields in CSV file here",
    required: false,
    isComplete: (campaign) => {
      if (campaign.general_data_source_type) return true;
      if (campaign?.[CAMPAIGN.METADATA]?.[CAMPAIGN.METADATA_HEADINGS]?.length) {
        return true;
      }
      return false;
    },
    component: (props) => <CampaignEditDataConnection {...props} />,
  },
  EMAIL_CONFIG: {
    label: "Details",
    title: "Tell us the details of your message",
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0, hasExternalUsersToggle=false) => {
      const notification = getNotification(campaign, notificationIndex);
      let isSystemNotifications = false;
      if([CAMPAIGN.IS_SYSTEM_NOTIFICATIONS] in campaign) {
        if(campaign?.[CAMPAIGN.IS_SYSTEM_NOTIFICATIONS]) {
          isSystemNotifications = true;
        } 
      }
      return isValidMailNotification(
        notification,
        getCampaignHeadings(campaign),
        hasExternalUsersToggle,
        getCampaignType(campaign),
        isSystemNotifications
      );
    },
    component: (props) => <CampaignEditDetails {...props} />,
    review: true,
    fields: [
      CONFIGURATIONS.SUBJECT,
      UI_DATA.SNIPPET_TEXT,
      CONFIGURATIONS.FROM,
    ],
  },
  // ZOOM_CONFIG: {
  //   label: "Details",
  //   title: "Tell us the details of your message",
  //   isComplete: (campaign, notificationIndex = 0) => {
  //     const notification = getNotification(campaign, notificationIndex);
  //     return isValidZoomTeamNotification(
  //       notification,
  //       getCampaignHeadings(campaign)
  //     );
  //   },
  //   component: (props) => <CampaignEditDetails {...props} />,
  //   review: true,
  //   fields: [RECIPIENTS.TO],
  // },
  EMAIL_TEMPLATE: {
    label: "Design",
    title: "Design your content",
    titlePostfix: " - Template needs to be saved.",
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0) => {
      const notification = getNotification(campaign, notificationIndex);
      if (getTemplateContent(notification)) {
        return true && !needToSaveTemplate;
      }
      return false;
    },
    component: (props) => <CampaignEditTemplate {...props} />,
    review: true,
  },
  ZOOM_TEMPLATE: {
    label: "Content",
    title: "Build your notification",
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0) => {
      const notification = getNotification(campaign, notificationIndex);
      if (getTemplateContent(notification)) {
        return true;
      }
      return false;
    },
    component: (props) => <CampaignEditTemplate {...props} />,
    review: true,
  },
  REVIEW: {
    label: "Review",
    isComplete: () => false,
    component: () => <CampaignEditReview />,
  },
};

export const STEPPER = {
  [NOTIFICATION_CHANNELS.mail.value]: {
    [CAMPAIGN_TYPES.general.value]: [
      STEPS.CHANNEL,
      STEPS.CAMPAIGN_TYPE,
      STEPS.GENERAL_DATA_CONNECT,
      STEPS.EMAIL_CONFIG,
      STEPS.EMAIL_TEMPLATE,
      STEPS.REVIEW,
    ],
    [CAMPAIGN_TYPES.personalized.value]: [
      STEPS.CHANNEL,
      STEPS.CAMPAIGN_TYPE,
      STEPS.DATA_CONNECT,
      STEPS.EMAIL_CONFIG,
      STEPS.EMAIL_TEMPLATE,
      STEPS.REVIEW,
    ],
  },
  [NOTIFICATION_CHANNELS.teams.value]: {
    [CAMPAIGN_TYPES.personalized.value]: [
      STEPS.CHANNEL,
      STEPS.DATA_CONNECT,
      STEPS.ZOOM_CONFIG,
      STEPS.ZOOM_TEMPLATE,
      STEPS.REVIEW,
    ],
  },
};

export const SYSTEM_NOTIFICATIONS_STEPPER = {
  [NOTIFICATION_CHANNELS.mail.value]: {
    [CAMPAIGN_TYPES.personalized.value]: [
      SYSTEM_NOTIFICATIONS_STEPS.CHANNEL,
      SYSTEM_NOTIFICATIONS_STEPS.CAMPAIGN_TYPE,
      SYSTEM_NOTIFICATIONS_STEPS.DATA_CONNECT,
      SYSTEM_NOTIFICATIONS_STEPS.EMAIL_CONFIG,
      SYSTEM_NOTIFICATIONS_STEPS.EMAIL_TEMPLATE,
      SYSTEM_NOTIFICATIONS_STEPS.REVIEW,
    ],
    [CAMPAIGN_TYPES.general.value]: [
      SYSTEM_NOTIFICATIONS_STEPS.CHANNEL,
      SYSTEM_NOTIFICATIONS_STEPS.CAMPAIGN_TYPE,
      SYSTEM_NOTIFICATIONS_STEPS.GENERAL_DATA_CONNECT,
      SYSTEM_NOTIFICATIONS_STEPS.EMAIL_CONFIG,
      SYSTEM_NOTIFICATIONS_STEPS.EMAIL_TEMPLATE,
      SYSTEM_NOTIFICATIONS_STEPS.REVIEW,
    ],
  },
  [NOTIFICATION_CHANNELS.teams.value]: {
    [CAMPAIGN_TYPES.personalized.value]: [
      SYSTEM_NOTIFICATIONS_STEPS.CHANNEL,
      // SYSTEM_NOTIFICATIONS_STEPS.DATA_CONNECT,
      // SYSTEM_NOTIFICATIONS_STEPS.ZOOM_CONFIG,
      SYSTEM_NOTIFICATIONS_STEPS.ZOOM_TEMPLATE,
      SYSTEM_NOTIFICATIONS_STEPS.REVIEW,
    ],
  },
};

export const DEFAULT_FLOW =
  STEPPER[NOTIFICATION_CHANNELS.mail.value][CAMPAIGN_TYPES.general.value];

export const SYSTEM_NOTIFICATIONS_DEFAULT_FLOW =
  SYSTEM_NOTIFICATIONS_STEPPER[NOTIFICATION_CHANNELS.mail.value][CAMPAIGN_TYPES.personalized.value];


// ------------------ V2 UI ------------------ //
export const STEPS_V2 = {
  DETAILS: {
    label: "Message details",
    title: "Add your message details",
    description: "Include essential fields and add attachments. Discover how to track message performance to measure engagement and effectiveness.",
    isComplete: () => {

    },
    isValid: (formValues = {}, errors = {}) => {
      const validationErrors = {};

      switch(formValues["channel"]) {
        case "mail":
          if (errors.from) {
            validationErrors.from = "From field is required";
          }
          if (errors.subject) {
            validationErrors.subject = "Subject field is required";
          }
          if(formValues.channel === NOTIFICATION_CHANNELS.mail.value && formValues.campaignType === CAMPAIGN_TYPES.personalized.value) {
            if (!formValues.hasExternalUsers) {
              validationErrors.hasExternalUsers = "Please confirm if the message is targeted to external users.";
            }
          }
        case "teams":
          break;
        default:
          break;
      }
      return {
        success: Object.keys(validationErrors).length === 0,
        errors: validationErrors
      };
    },
    component: (props) => <MessageDetailsEdit {...props} />,
    icon: <Email size="medium" />,
  },
  RECIPIENTS: {
    label: "Select recipients",
    title: "Select recipients for your message",
    description: "Add recipients using one or more methods: enter them manually, select from saved recipients, apply filters, or upload CSV file.",
    isComplete: () => {

    },
    isValid: (formValues, errors) => {
      const validationErrors = {};
      const DATA_SOURCE_ERR_MSG = "Please select a valid data source type.";
      const CSV_ERR_MSG = "Please upload a CSV file or select the 'TO' field mappings.";
      switch(formValues.campaignType) {
        case CAMPAIGN_TYPES.general.value: {
          switch(formValues.dataSourceType) {
            case DATA_SOURCE_TYPES.MANUAL:
              if(!formValues.to || formValues.to.length <= 0) {
                validationErrors.to = "Please select the 'TO' recipients.";
              }
              break;
            case DATA_SOURCE_TYPES.CSV:
              if(!formValues.csvFiles || formValues.csvFiles.length <= 0 || !formValues.to || typeof formValues.to !== "string") {
                validationErrors.csvFiles = CSV_ERR_MSG;
              }
              break;
            case DATA_SOURCE_TYPES.SSOT:
              if(!formValues.ssot) {
                validationErrors.ssot = "Please select a SSOT data source.";
              } else if (!formValues.ssot?.["filter"]) {
                validationErrors.ssot = "Please select a valid filter for the SSOT data source.";
              }
              break;
            default:
              validationErrors.dataSourceType = DATA_SOURCE_ERR_MSG;
          }
        }
        break;
        case CAMPAIGN_TYPES.personalized.value:
          if(!formValues.dataSourceType) {
            validationErrors.dataSourceType = DATA_SOURCE_ERR_MSG;
          }
          if(formValues.isSystemNotifications) {
            if(!formValues.csvFiles || formValues.csvFiles.length <= 0) {
              validationErrors.csvFiles = "Please upload a CSV file.";
            }
          } else {
            if(!formValues.csvFiles || formValues.csvFiles.length <= 0 || !formValues.to || typeof formValues.to !== "string") {
              validationErrors.csvFiles = CSV_ERR_MSG;
            }
          }
          break;
        default:
          break;
      }
      
      return {
        success: Object.keys(validationErrors).length === 0,
        errors: validationErrors
      };
    },
    component: (props) => <SelectRecipientsEdit {...props} />,
    icon: <User size="medium" />,
  },
  CONTENT: {
    label: "Design content",
    title: "Design your message content",
    description: "The supporting body text should be clear, meaningful, and helpful, guiding users to understand what actions they can take.",
    isComplete: (campaign, needToSaveTemplate = false, notificationIndex = 0) => {
      const notification = getNotification(campaign, notificationIndex);
      if (getTemplateContent(notification)) {
        return true && !needToSaveTemplate;
      }
      return false;
    },
    isValid: (formValues, errors, needToSaveTemplate) => {
      const validationErrors = {};
      if(!formValues.templateContent) {
        validationErrors.templateContent = "No templates have been selected.";
      } else if (errors.templateContent) {
        validationErrors.templateContent = errors.templateContent;
      }
      // Check if there are unsaved template changes (happens when user edits tracking settings after creating the template)
      if(Object.keys(validationErrors).length === 0 && needToSaveTemplate) {
        validationErrors.templateContent = "There are some unsaved changes present in the template, please open and save the template before proceeding.";
      }
      
      return {
        success: Object.keys(validationErrors).length === 0,
        errors: validationErrors
      };
    },
    component: (props) => <DesignContentEdit {...props} />,
    icon: <Note size="medium" />,
  },
  SCHEDULE: {
    label: "Delivery time",
    title: "Set delivery time for your message",
    description: "Choose to send your message immediately or schedule it for a specific date and time - ensuring it reaches recipients at the most effective moment.",
    isComplete: () => {

    },
    isValid: (formValues, errors) => {
      const validationErrors = {};
      
      if (!formValues["deliveryType"] || errors.deliveryTime) {
        validationErrors.deliveryType = "Delivery type is required.";
      } else if (formValues["deliveryType"] === "scheduled") {
        const requiredFields = ["scheduledDate", "scheduledTime", "scheduledDateTime"];
        for (const field of requiredFields) {
          if (!formValues[field]) {
            validationErrors[field] = "This field is required.";
          }
        }
        if(!validateTime(formValues["scheduledDateTime"])) {
          validationErrors.scheduledDateTime = "Scheduled date and time must be at least 1 hour in the future.";
        }
      }
      
      return {
        success: Object.keys(validationErrors).length === 0,
        errors: validationErrors
      };
    },
    component: (props) => <DeliveryTimeEdit {...props} />,
    icon: <Clock size="medium" />
  },
  REVIEW: {
    label: "Review message",
    title: "Review your message",
    description: "Review the drafted details and templates to ensure accuracy and make any necessary adjustments before finalizing.",
    isComplete: () => {

    },
    isValid: () => {
      return {
        success: true,
        errors: {}
      };
    },
    component: (props) => <ReviewMessageEdit {...props} />,
    icon: <Eye size="medium" />,
  },
  BUILD_TEAMS_NOTIFICATIONS: {
    label: "Build notifications",
    title: "Build notifications",
    description: "Create a Teams template using MS Adaptive Cards, then copy it into the bot code to complete the template.",
    isComplete: () => {},
    isValid: (formValues, errors) => {
      const validationErrors = {};
      
      if(!formValues.templateContent || errors.templateContent) {
        validationErrors.templateContent = "Bot code is required.";
      }
      
      return {
        success: Object.keys(validationErrors).length === 0,
        errors: validationErrors
      };
    },
    component: (props) => <MsTeamsNotificationEdit {...props} />,
    icon: <Note size="medium" />,
  }
}

export const CAMPAIGN_EDIT_STEPS = {
  [NOTIFICATION_CHANNELS.mail.value]: [
      STEPS_V2.DETAILS,
      STEPS_V2.RECIPIENTS,
      STEPS_V2.CONTENT,
      STEPS_V2.SCHEDULE,
      STEPS_V2.REVIEW,
  ],
  [NOTIFICATION_CHANNELS.teams.value]: [
      STEPS_V2.RECIPIENTS,
      STEPS_V2.BUILD_TEAMS_NOTIFICATIONS,
      STEPS_V2.SCHEDULE,
      STEPS_V2.REVIEW,
  ],
};

export const SYSTEM_NOTIFICATIONS_CAMPAIGN_EDIT_STEPS = {
  [NOTIFICATION_CHANNELS.mail.value]: [
      STEPS_V2.DETAILS,
      STEPS_V2.RECIPIENTS,
      STEPS_V2.CONTENT,
      STEPS_V2.REVIEW,
  ],
  [NOTIFICATION_CHANNELS.teams.value]: [
      STEPS_V2.RECIPIENTS,
      STEPS_V2.BUILD_TEAMS_NOTIFICATIONS,
      STEPS_V2.REVIEW,
  ],
};

export const CAMPAIGN_EDIT_STEP_VALIDATION_STATE = {
  COMPLETED: 1,
  ERROR: 0,
  IN_PROGRESS: -1,
  NOT_STARTED: -2,
}

export const campaignFormSchema = yup.object().shape({
  from: yup.string().required("From field is required"),
  subject: yup.string().required("Subject field is required")
                        .max(SUBJECT_MAX_CHAR, `Subject cannot exceed ${SUBJECT_MAX_CHAR} characters`),
  priority: yup.string().required("Message priority is required"),
  snippet: yup.string().max(SNIPPET_MAX_CHAR, `Snippet cannot exceed ${SNIPPET_MAX_CHAR} characters`),
  personaliseMessage: yup.boolean().required(),
  channel: yup.string().required(),
  hasExternalUsers: yup.string(),
  templateContent: yup.string().required("No templates have been selected."),
});